const pendingRequests = new Map<string, Promise<Response>>();

export function escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
}

export function isValidUrl(url: string): boolean {
    try {
        const parsed = new URL(url);
        return parsed.protocol === 'http:' || parsed.protocol === 'https:';
    } catch {
        return false;
    }
}

export function formatTimeAgo(isoDate: string): string {
    const date = new Date(isoDate);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffH = Math.floor(diffMin / 60);
    if (diffH < 24) return `${diffH}h ago`;
    const diffD = Math.floor(diffH / 24);
    return `${diffD}d ago`;
}

// --- Network resilience ---
//
// Widgets poll on timers that keep firing while the machine sleeps, the Wi-Fi
// drops or DNS briefly fails. Without this layer every one of those ticks turns
// into an unhandled `TypeError: Failed to fetch` in the console and wipes the
// widget's last-known-good content. Transient failures are therefore retried,
// suppressed while offline, and logged once per outage instead of once per tick.

const RETRY_DELAYS_MS = [800, 2400];
const REQUEST_TIMEOUT_MS = 15000;
const OFFLINE_MESSAGE = 'Ei verkkoyhteyttä — yritetään uudelleen.';

/** A connectivity problem (offline, DNS, timeout) rather than a real API error. */
export class NetworkError extends Error {
    readonly offline: boolean;
    readonly reason: unknown;

    constructor(message: string, offline: boolean, reason?: unknown) {
        super(message);
        this.name = 'NetworkError';
        this.offline = offline;
        this.reason = reason;
    }
}

export function isNetworkError(error: unknown): boolean {
    return error instanceof NetworkError;
}

export function isOffline(): boolean {
    return navigator.onLine === false;
}

function isTransient(error: unknown): boolean {
    // fetch() rejects with TypeError for DNS/connection failures; AbortError for timeouts.
    return error instanceof TypeError || (error instanceof DOMException && error.name === 'AbortError');
}

const delay = (ms: number): Promise<void> => new Promise(resolve => setTimeout(resolve, ms));

async function requestWithRetry(url: string, init?: RequestInit): Promise<Response> {
    if (isOffline()) {
        throw new NetworkError(`Offline, skipping request to ${url}`, true);
    }

    let lastError: unknown;
    for (let attempt = 0; attempt <= RETRY_DELAYS_MS.length; attempt++) {
        try {
            return await fetch(url, {
                cache: 'no-store',
                signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
                ...init,
            });
        } catch (error) {
            lastError = error;
            if (!isTransient(error)) throw error;
            if (attempt === RETRY_DELAYS_MS.length || isOffline()) break;
            await delay(RETRY_DELAYS_MS[attempt]);
        }
    }

    throw new NetworkError(`Request to ${url} failed`, isOffline(), lastError);
}

/**
 * Fetches an API endpoint with in-flight deduplication, a request timeout and
 * retries for transient connectivity failures. Rejects with NetworkError when
 * the network — rather than the endpoint — is at fault.
 */
export function apiFetch(url: string, init?: RequestInit): Promise<Response> {
    const key = `${init?.method ?? 'GET'} ${url}`;
    const existing = pendingRequests.get(key);
    if (existing) return existing;
    const promise = requestWithRetry(url, init).finally(() => pendingRequests.delete(key));
    pendingRequests.set(key, promise);
    return promise;
}

// --- Shared transit rendering ---

interface TransitAlert {
    header: string;
}

interface TransitDeparture {
    route: string;
    headsign: string;
    realtimeDeparture: number;
    realtime: boolean;
}

export function renderTransitWidget(
    container: HTMLElement,
    alerts: TransitAlert[] | undefined,
    departures: TransitDeparture[] | undefined,
    cssPrefix: string,
    emptyMessage: string
): void {
    let html = '';

    if (alerts && alerts.length > 0) {
        const alertsHtml = alerts
            .map(a => `<div class="${cssPrefix}-alert"><span class="${cssPrefix}-alert-icon">&#9888;</span> ${escapeHtml(a.header)}</div>`)
            .join('');
        html += `<div class="${cssPrefix}-alerts">${alertsHtml}</div>`;
    }

    if (!departures || departures.length === 0) {
        html += `<p class="${cssPrefix}-error">${escapeHtml(emptyMessage)}</p>`;
        container.innerHTML = html;
        markLoaded(container, cssPrefix);
        return;
    }

    const rows = departures.map(dep => {
        const time = new Date(dep.realtimeDeparture * 1000).toLocaleTimeString('fi-FI', {
            hour: '2-digit',
            minute: '2-digit',
        });
        const rtDot = dep.realtime ? `<span class="${cssPrefix}-rt">&#9679;</span>` : '';
        return `<div class="${cssPrefix}-row">
            <span class="${cssPrefix}-route">${escapeHtml(dep.route)}</span>
            <span class="${cssPrefix}-headsign">${escapeHtml(dep.headsign)}</span>
            <span class="${cssPrefix}-time">${rtDot}${time}</span>
        </div>`;
    }).join('');

    html += rows;
    container.innerHTML = html;
    markLoaded(container, cssPrefix);
}

// --- Shared error rendering ---

const failingWidgets = new Set<string>();

/**
 * Renders a widget failure. Connectivity blips keep the last-known-good content
 * (dimmed) instead of blanking the widget, and are logged only on the first
 * failure of an outage so repeated poll ticks cannot flood the console.
 */
export function renderError(container: HTMLElement, cssPrefix: string, message: string, error?: unknown): void {
    const transient = isNetworkError(error);
    const hasContent = container.dataset.loaded === 'true';

    if (!transient) {
        failingWidgets.delete(cssPrefix);
        if (error) console.error(message, error);
        container.dataset.loaded = 'false';
        container.classList.remove('is-stale');
        container.innerHTML = `<p class="${cssPrefix}-error">${escapeHtml(message)}</p>`;
        return;
    }

    if (!failingWidgets.has(cssPrefix)) {
        failingWidgets.add(cssPrefix);
        console.warn(`${cssPrefix}: network unavailable, keeping last known data.`);
    }

    if (hasContent) {
        container.classList.add('is-stale');
        return;
    }

    container.innerHTML = `<p class="${cssPrefix}-error">${escapeHtml(OFFLINE_MESSAGE)}</p>`;
}

/** Marks a widget's content as current, clearing any stale/failure state. */
export function markLoaded(container: HTMLElement, cssPrefix: string): void {
    failingWidgets.delete(cssPrefix);
    container.dataset.loaded = 'true';
    container.classList.remove('is-stale');
}

// --- Shared modal creation ---

export function createModal(id: string, className: string, innerHtml: string): HTMLDivElement {
    const existing = document.getElementById(id);
    if (existing) existing.remove();

    const modal = document.createElement('div');
    modal.id = id;
    modal.className = className;
    modal.innerHTML = innerHtml;

    modal.querySelector('[data-modal-close]')?.addEventListener('click', () => modal.remove());
    modal.addEventListener('click', (e: Event) => {
        if (e.target === modal) modal.remove();
    });

    document.body.appendChild(modal);
    return modal;
}
